import { Store01Icon, UserMultiple02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { api } from "@slapinc/slappos-backend/api";
import {
	createFileRoute,
	useNavigate,
	useSearch,
} from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import type { GenericId as Id } from "convex/values";
import { useCallback, useState } from "react";
import { BranchSelector } from "@/components/branch-selector";
import { Can } from "@/components/can";
import { PageContent } from "@/components/layout/page-content";
import { CreateStaffModal } from "@/components/modals/create-staff-modal";
import { EditStaffModal } from "@/components/modals/edit-staff-modal";
import { ActionHeaderButton } from "@/components/ui/action-header-button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyStateWithSkeleton } from "@/components/ui/empty-state-with-skeleton";
import { StaffTable } from "@/features/staff/_components/staff-table";
import { useAutoSelectBranch } from "@/hooks/use-auto-select-branch";
import { useMe } from "@/hooks/use-me";
import { usePagination } from "@/hooks/use-pagination";
import { handleConvexError } from "@/lib/errors";
import type { StaffFormInput } from "@/lib/schemas/staff";
import { toast } from "@/lib/toast";
import type { Staff } from "@/lib/types/staff";

interface StaffSearch {
	branch?: string;
}

export const Route = createFileRoute("/(dashboard)/branch/staff")({
	component: StaffPage,
	validateSearch: (search: Record<string, unknown>): StaffSearch => ({
		branch: search.branch as string | undefined,
	}),
});

const PAGE_SIZE = 10;

function StaffPage() {
	const navigate = useNavigate();
	const { branch: searchBranchId } = useSearch({
		from: "/(dashboard)/branch/staff",
	});

	// Pagination
	const { pagination, onPaginationChange, resetPage } =
		usePagination(PAGE_SIZE);

	// Modal state
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [staffToDelete, setStaffToDelete] = useState<Staff | null>(null);

	// Loading states
	const [isCreating, setIsCreating] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [branchSelectorOpen, setBranchSelectorOpen] = useState(false);

	// Get current user for org context
	const me = useMe();
	const orgId = me?.activeOrganizationId;

	const branchesData = useQuery(
		api.features.branches.web.list,
		orgId ? { orgId } : "skip",
	);
	const branches = branchesData ?? [];

	const { branchId, isLoading: isLoadingBranches } = useAutoSelectBranch({
		branchId: searchBranchId,
		branches: branchesData,
		orgId,
	});

	// Query staff for selected branch
	const staffData = useQuery(
		api.features.staff.web.list,
		orgId && branchId
			? { orgId, branchId: branchId as Id<"branches"> }
			: "skip",
	);

	const staff = (staffData ?? []) as Staff[];
	const isLoading =
		isLoadingBranches || (staffData === undefined && !!branchId);

	// Mutations
	const createStaff = useMutation(api.features.staff.web.create);
	const updateStaff = useMutation(api.features.staff.web.update);
	const removeStaff = useMutation(api.features.staff.web.remove);

	// Paginate staff (client-side)
	const paginatedStaff = staff.slice(
		pagination.pageIndex * pagination.pageSize,
		(pagination.pageIndex + 1) * pagination.pageSize,
	);

	// Handle branch selection
	const handleBranchChange = useCallback(
		(value: Id<"branches"> | Id<"branches">[] | undefined) => {
			if (typeof value !== "string") return;
			navigate({
				to: ".",
				search: (prev: StaffSearch) => ({ ...prev, branch: value }),
			});
			resetPage();
		},
		[navigate, resetPage],
	);

	// Handlers
	const handleCreate = useCallback(
		async (data: StaffFormInput) => {
			if (!orgId || !branchId) return;

			setIsCreating(true);
			try {
				await createStaff({
					orgId,
					branchId: branchId as Id<"branches">,
					name: data.name,
					email: data.email || undefined,
					pin: data.pin || undefined,
				});
				toast.success(<>Staff member added</>);
			} catch (error) {
				handleConvexError(error, "Failed to add staff member");
				throw error;
			} finally {
				setIsCreating(false);
			}
		},
		[orgId, branchId, createStaff],
	);

	const handleEdit = useCallback((staffMember: Staff) => {
		setSelectedStaff(staffMember);
		setEditModalOpen(true);
	}, []);

	const handleUpdate = useCallback(
		async (data: StaffFormInput) => {
			if (!orgId || !branchId || !selectedStaff) return;

			setIsUpdating(true);
			try {
				await updateStaff({
					orgId,
					branchId: branchId as Id<"branches">,
					staffId: selectedStaff._id,
					name: data.name,
					email: data.email || undefined,
					pin: data.pin || undefined,
				});
				toast.success(<>Staff member updated</>);
			} catch (error) {
				handleConvexError(error, "Failed to update staff member");
				throw error;
			} finally {
				setIsUpdating(false);
			}
		},
		[orgId, branchId, selectedStaff, updateStaff],
	);

	const handleDelete = useCallback((staffMember: Staff) => {
		setStaffToDelete(staffMember);
		setDeleteDialogOpen(true);
	}, []);

	const confirmDelete = useCallback(async () => {
		if (!orgId || !branchId || !staffToDelete) return;

		setIsDeleting(true);
		try {
			await removeStaff({
				orgId,
				branchId: branchId as Id<"branches">,
				staffId: staffToDelete._id,
			});
			toast.success(<>Staff member removed</>);
			setDeleteDialogOpen(false);
			setStaffToDelete(null);
		} catch (error) {
			handleConvexError(error, "Failed to remove staff member");
		} finally {
			setIsDeleting(false);
		}
	}, [orgId, branchId, staffToDelete, removeStaff]);

	return (
		<PageContent
			title="Staff"
			controls={
				<div className="flex items-center gap-3">
					<BranchSelector
						branches={branches}
						value={branchId as Id<"branches"> | undefined}
						onValueChange={handleBranchChange}
						placeholder="Select branch"
						className="w-48"
						open={branchSelectorOpen}
						onOpenChange={setBranchSelectorOpen}
						onViewAll={() => navigate({ to: "/settings/branches" })}
						onCreateNew={() => navigate({ to: "/settings/branches" })}
					/>
					{branchId && (
						<Can scope="staff.create">
							<ActionHeaderButton
								label="Add Staff"
								onClick={() => setCreateModalOpen(true)}
							/>
						</Can>
					)}
				</div>
			}
		>
			<div className="flex flex-1 flex-col gap-4 sm:gap-6">
				{!branchId && !isLoadingBranches ? (
					<div className="flex flex-1 flex-col justify-end pb-12">
						<EmptyStateWithSkeleton
							title="No branches found"
							description="Get started by creating your first branch to view staff members."
							actionLabel="Create branch"
							onAction={() => navigate({ to: "/settings/branches" })}
							icon={
								<HugeiconsIcon
									icon={Store01Icon}
									className="h-5 w-5 text-neutral-700"
									strokeWidth={2.2}
								/>
							}
						/>
					</div>
				) : branchId && !isLoading && staff.length === 0 ? (
					<div className="flex flex-1 flex-col justify-end pb-12">
						<EmptyStateWithSkeleton
							title="No staff members found"
							description="Add staff members to this branch to manage permissions and track their activity"
							actionLabel="Add Staff"
							onAction={() => setCreateModalOpen(true)}
							icon={
								<HugeiconsIcon
									icon={UserMultiple02Icon}
									className="h-5 w-5 text-neutral-700"
									strokeWidth={2.2}
								/>
							}
						/>
					</div>
				) : (
					<StaffTable
						data={paginatedStaff}
						loading={isLoading}
						pagination={pagination}
						onPaginationChange={onPaginationChange}
						rowCount={staff.length}
						onEdit={handleEdit}
						onDelete={handleDelete}
					/>
				)}
			</div>

			{/* Create Modal */}
			<CreateStaffModal
				open={createModalOpen}
				onOpenChange={setCreateModalOpen}
				onSubmit={handleCreate}
				isSubmitting={isCreating}
			/>

			{/* Edit Modal */}
			<EditStaffModal
				open={editModalOpen}
				onOpenChange={setEditModalOpen}
				staff={selectedStaff}
				onSubmit={handleUpdate}
				isSubmitting={isUpdating}
			/>

			{/* Delete Confirmation */}
			<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Remove staff member?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to remove "{staffToDelete?.name}"? This
							action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={confirmDelete}
							disabled={isDeleting}
						>
							{isDeleting ? "Removing..." : "Remove"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</PageContent>
	);
}
